import { useEffect, useState } from "react";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBookCall } from "../components/BookCallDialog";
import { Calendar as CalendarIcon, MapPin, Phone, FileText } from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-[#F5F3EF] text-[#5C635A] border-[#E5E2DC]",
  confirmed: "bg-[#1C3326]/10 text-[#1C3326] border-[#1C3326]/30",
  completed: "bg-[#C26D5C]/10 text-[#C26D5C] border-[#C26D5C]/40",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

export default function UserDashboard() {
  const { user } = useAuth();
  const { openDialog } = useBookCall();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/bookings/me");
        setBookings(res.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col">
      <SiteHeader />
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 sm:px-8 md:px-12 py-16" data-testid="user-dashboard">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="tag-line text-[#C26D5C] mb-3">Your dashboard</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#1A201C] tracking-tight">
              Hello, {user?.name?.split(" ")[0] || "there"}.
            </h1>
            <p className="text-[#5C635A] mt-3 font-light">Here's everything we're preparing for you.</p>
          </div>
          <Button
            onClick={() => openDialog({ source: "dashboard", service: "Discovery call" })}
            className="rounded-sm bg-[#1C3326] hover:bg-[#2A4A38] text-[#FAF9F6]"
            data-testid="dashboard-book-btn"
          >
            + New request
          </Button>
        </div>

        {loading ? (
          <p className="text-[#5C635A]">Loading your bookings...</p>
        ) : bookings.length === 0 ? (
          <div className="bg-white border border-[#E5E2DC] p-12 text-center">
            <h3 className="font-serif text-2xl text-[#1A201C] mb-3">No bookings yet</h3>
            <p className="text-[#5C635A] mb-6">Start with a free 20-minute consultation.</p>
            <Button
              onClick={() => openDialog({ source: "dashboard-empty", service: "Discovery call" })}
              className="rounded-sm bg-[#1C3326] hover:bg-[#2A4A38] text-[#FAF9F6]"
              data-testid="empty-book-btn"
            >
              Send your first request
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {bookings.map((b) => (
              <div key={b.booking_id} className="bg-white border border-[#E5E2DC] p-7 transition-all hover:-translate-y-1 hover:shadow-md" data-testid={`booking-card-${b.booking_id}`}>
                <div className="flex items-start justify-between mb-5">
                  <div>
                    <p className="tag-line text-[#5C635A] mb-1">{b.service_type}</p>
                    <h3 className="font-serif text-2xl text-[#1A201C]">{b.preferred_date}</h3>
                    <p className="text-[#5C635A] text-sm">at {b.preferred_time}</p>
                  </div>
                  <Badge variant="outline" className={`rounded-sm capitalize ${STATUS_STYLES[b.status]}`} data-testid={`status-${b.booking_id}`}>
                    {b.status}
                  </Badge>
                </div>
                <div className="space-y-2 text-sm text-[#5C635A]">
                  <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} /> {b.apartment_address}</div>
                  <div className="flex items-center gap-2"><Phone className="w-4 h-4" strokeWidth={1.5} /> {b.phone}</div>
                  {b.notes && <div className="flex items-start gap-2"><FileText className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} /> {b.notes}</div>}
                  <div className="flex items-center gap-2"><CalendarIcon className="w-4 h-4" strokeWidth={1.5} /> Submitted {new Date(b.created_at).toLocaleDateString()}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
